import axios from 'axios'

import { api } from './client'
import type { TeaGrade, TeaPattern } from './tea-purchase-api'
import { filenameFromContentDisposition } from '@/lib/download-blob'

/** Một phiếu cân tươi nguồn của phiếu khô (quantity = kg TƯƠI). */
export interface DriedTeaPurchaseSource {
    tea_purchase_id: string
    code: string
    quantity: number
    purchase_date: string
    /** Giống chè của phiếu tươi nguồn (phiếu khô không tự mang giống từ M-27). */
    tea_type_name?: string
}

/**
 * DriedTeaPurchase là phiếu cân chè KHÔ: mua từ hộ kinh doanh (sources rỗng)
 * hoặc gộp từ N phiếu cân tươi đã duyệt của cùng một hộ. Phiếu nhập kho lô phân
 * loại lấy kg trực tiếp từ phiếu này (M-27).
 */
export interface DriedTeaPurchase {
    id: string
    code: string
    grower_id: string
    /** Phiếu mới không còn loại chè (M-27). */
    tea_type_id?: string | null
    purchase_date: string
    /** Kg KHÔ. */
    quantity: number
    tea_grade?: TeaGrade | null
    tea_pattern?: TeaPattern | null
    note?: string | null
    /** Hộ đã trả tờ phiếu cân giấy (cờ một chiều, chuyển từ phiếu tươi ở M-81). */
    ballot_returned?: boolean
    created_by?: string | null
    created_at: string
    updated_at: string
    // hydrated read-side
    grower_name?: string
    grower_phone?: string
    grower_address?: string
    tea_type_name?: string
    created_by_name?: string
    sources?: DriedTeaPurchaseSource[]
    /** Kg khô còn lại chưa dùng ở phiếu nhập kho lô phân loại. */
    remaining_dry_kg?: number | null
    /** Đã là nguồn của PNK lô phân loại chờ duyệt/đã duyệt → khoá sửa. */
    has_active_in_note: boolean
    /**
     * Mã các PNK lô phân loại khai phiếu này làm nguồn, ngăn bằng ", "
     * (rỗng = chưa dùng). Đã dùng = không xoá được phiếu khô nữa.
     */
    sorting_note_codes?: string
}

export interface ListDriedTeaPurchasesParams {
    grower_id?: string
    tea_type_id?: string
    search?: string
    /** Chỉ phiếu còn kg khô chưa nhập hết vào lô (picker PNK). */
    only_with_remaining?: boolean
    date_from?: string
    date_to?: string
    page?: number
    per_page?: number
}

export interface CreateDriedTeaPurchaseRequest {
    grower_id: string
    purchase_date?: string
    /** Kg khô, nhập tay (không tự tính từ tổng kg tươi nguồn). */
    quantity: number
    /** Nhãn "Trạng thái", tuỳ chọn. Hộ kinh doanh không có trạng thái. */
    tea_grade?: TeaGrade
    note?: string
    /** Tick "Mua chè từ hộ kinh doanh" — true thì source_ids phải rỗng. */
    self_dried: boolean
    source_ids: string[]
}

export interface PatchDriedTeaPurchaseRequest {
    grower_id?: string
    tea_type_id?: string
    purchase_date?: string
    quantity?: number
    /** '' = xoá trạng thái. */
    tea_grade?: TeaGrade | ''
    tea_pattern?: TeaPattern
    note?: string
    /** Gửi self_dried/source_ids = đổi nguồn (thay toàn bộ danh sách). */
    self_dried?: boolean
    source_ids?: string[]
}

export const driedTeaPurchaseApi = {
    list: (params?: ListDriedTeaPurchasesParams) =>
        api.get<DriedTeaPurchase[]>('/dried-tea-purchases', {
            params: params as Record<
                string,
                string | number | boolean | undefined
            >,
        }),
    get: (id: string) =>
        api.get<DriedTeaPurchase>(`/dried-tea-purchases/${id}`),
    create: (data: CreateDriedTeaPurchaseRequest) =>
        api.post<DriedTeaPurchase>('/dried-tea-purchases', data),
    /**
     * Tạo nhiều phiếu trong MỘT transaction. Lỗi của một dòng trả 400 mã
     * BULK_ROW_INVALID, `details.row` là vị trí dòng trong `items` (đếm từ 0).
     */
    bulkCreate: (items: CreateDriedTeaPurchaseRequest[]) =>
        api.post<DriedTeaPurchase[]>('/dried-tea-purchases/bulk', { items }),
    patch: (id: string, data: PatchDriedTeaPurchaseRequest) =>
        api.patch<DriedTeaPurchase>(`/dried-tea-purchases/${id}`, data),
    /** Xoá phiếu nhập sai. 409 khi phiếu đã là nguồn của PNK lô phân loại. */
    delete: (id: string) => api.delete<void>(`/dried-tea-purchases/${id}`),
    markBallotReturned: (id: string) =>
        api.post<DriedTeaPurchase>(`/dried-tea-purchases/${id}/ballot`, {}),

    /** Download Excel "BÁO CÁO CHÈ THÀNH PHẨM" theo filter đang lọc. */
    exportExcel: async (
        params: ListDriedTeaPurchasesParams,
    ): Promise<{ blob: Blob; filename?: string }> => {
        const res = await axios.get<Blob>(
            '/api/v1/dried-tea-purchases/export',
            {
                params,
                responseType: 'blob',
                withCredentials: true,
            },
        )
        return {
            blob: res.data,
            filename: filenameFromContentDisposition(
                (res.headers as Record<string, string | undefined>)[
                    'content-disposition'
                ],
            ),
        }
    },
}
